/* eslint-disable */
import React, {
  RefObject,
  createRef,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Editor } from '@ckeditor/ckeditor5-core';
import { Element } from '@ckeditor/ckeditor5-engine';
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
import 'react-contexify/ReactContexify.css';
import { v4 as uuidv4 } from 'uuid';
import { ElementDefinition, TagAttributesDefinition } from './Definition';
import { Droppable } from './Droppable';
import Schema from './Schema';
import { SectionDataTreeNode } from './SectionDataTreeNode';
import { UpdateElementHandler } from './SectionDataTreeNodeInterface';
import { ValidationResults } from './ValidationResults';

interface DataTreeElementNodeProps {
  editor: Editor;
  element: Element;
  selectedElement?: Node;
  setSelectedElement: (selectedElement?: Node) => void;
  expandingItems: Element[];
  setExpandingItems: (expandingItems: Element[]) => void;
  handleSelectClick: (node: Node, e: React.MouseEvent) => void;
  updateComponent: (ancestor: number) => void;
  ghost: boolean;
  draggingNode: Node | null;
  isDragging: boolean;
  showContextMenu: (node: Node, e: React.MouseEvent) => void;
  viewAttributes: string[];
  scrollIntoElement: Node | undefined;
  resetScrollIntoElement: () => void;
  validationResults: ValidationResults;
}

const SectionDataTreeElementNode = forwardRef<UpdateElementHandler, DataTreeElementNodeProps>(
  (
    {
      editor,
      element,
      selectedElement,
      setSelectedElement,
      expandingItems,
      setExpandingItems,
      handleSelectClick,
      updateComponent,
      ghost,
      draggingNode,
      isDragging,
      showContextMenu,
      viewAttributes,
      scrollIntoElement,
      resetScrollIntoElement,
      validationResults,
    },
    ref
  ) => {
    const id = useId();

    /**
     * span要素参照。
     */
    const spanRef = useRef<HTMLSpanElement>(null);

    /**
     * 更新用ステート変数。
     */
    const [counter, setCounter] = useState<number>(0);
    const updateComponentLocal = () => {
      setCounter(counter + 1);
    };

    /**
     * tagAttributeとは、以下のような構造になっているmodel要素に付加されているview上の要素に対応した属性を格納しているもの。
     * attributesやclassesは必ずあるとは限らない。
     * [
     *  'htmlDivAttributes',
     *  {
     *   attributes: {},
     *   classes: [],
     *  }
     * ]
     */

    // 要素の取り得る属性定義を取得
    const elementDefinition = useMemo(() => {
      return Schema.getElementDefinition(element.name);
    }, [element.name]);
    const tagAttributesDefinitions = [] as TagAttributesDefinition[];
    if (elementDefinition?.editableAttributes) {
      for (const editableAttribute of elementDefinition?.editableAttributes) {
        const tagAttributesDefinition = Schema.getTagAttributesDefinition(editableAttribute.name);
        if (tagAttributesDefinition) {
          tagAttributesDefinitions.push(tagAttributesDefinition);
        }
      }
    }

    /**
     * スクロール対象要素が自分であれば、スクロールして表示する。
     */
    useEffect(() => {
      if (element === scrollIntoElement && spanRef.current) {
        spanRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 一度スクロールしたら、スクロール対象をリセットする。
        resetScrollIntoElement();
      }
    }, [scrollIntoElement]);

    /**
     * 属性値を取得する。
     * すべてを見ていって、最初に見つかったものを返す。
     * @param element 要素。
     * @param name 属性名。
     * @returns 属性値。存在しなければundefined。
     */
    const getAttribute = (element: Element, name: string): any => {
      let value = element.getAttribute(name);
      if (value) {
        return value;
      }

      for (const tagAttributesDefinition of tagAttributesDefinitions) {
        const attributesValue = element.getAttribute(tagAttributesDefinition.model) as any;
        if (attributesValue) {
          value = attributesValue[name];
          if (value) {
            return value;
          }

          if (attributesValue['attributes']) {
            value = attributesValue['attributes'][name];
            if (value) {
              return value;
            }
          }
        }
      }

      return undefined;
    };

    /**
     * ツリー上のタグ名部分を生成する。
     */
    const tagName = useMemo(() => {
      let tagDisplayName = elementDefinition ? elementDefinition.view.display : <>`view定義なし: ${element.name}`</>;
      switch (element.name) {
        case 'tableCell':
          const htmlThAttributes = element.getAttribute('htmlThAttributes');
          if (htmlThAttributes) {
            tagDisplayName = (
              <>
                <strong>th</strong>
              </>
            );
          }
          break;
      }
      let classes = getAttribute(element, 'classes') ? '.' + getAttribute(element, 'classes').join('.') : '';
      return (
        <>
          {tagDisplayName}
          {classes}
        </>
      );
    }, [element, elementDefinition]);

    /**
     * 特別に表示する属性値文字列
     */
    const displayAttributesString = useMemo(() => {
      return viewAttributes.reduce((acc, current) => {
        const attributeValue = getAttribute(element, current);
        if (attributeValue) {
          acc += ` @${current}="${attributeValue}"`;
        }

        return acc;
      }, '');
    }, [element, viewAttributes]);

    /**
     * 表示幅を調整するための文字列。
     */
    const displayWidthString = '　'.repeat(100);

    const childElements = Array.from(element.getChildren());
    const childElementRefs = useRef<RefObject<UpdateElementHandler>[]>([]);
    childElements.forEach((_, index) => {
      childElementRefs.current[index] = createRef<UpdateElementHandler>();
    });

    /**
     * バリデーションを行う。
     * @param childElements 子要素リスト。
     * @param definition 定義。
     * @returns バリデーション結果。
     */
    const validate = (element: Element, definition?: ElementDefinition): [boolean, string] => {
      let result = true;
      if (!definition || !element) {
        return [true, ''];
      }

      if (definition.validate) {
        return definition.validate(element);
      }

      return [false, 'バリデーション関数が定義されていません。'];
    };
    const [validationResult, validationMessage] = useMemo(() => {
      validationResults.clearIndividualValidationResult(element);
      let [validationResult, validationMessage] = validate(element, elementDefinition);
      if (!validationResult) {
        validationResults.pushIndividualValidationResult(element, validationMessage);
      }

      if (validationResults.hasMessages(element)) {
        return [false, validationResults.getMessage(element)];
      }

      return [true, ''];
    }, [element, childElements, elementDefinition, validationResults.hasMessages(element)]);

    /**
     * 展開・閉じるボタンのクリックイベントハンドラ。
     * @param e イベントパラメータ。
     */
    const handleToggleClick = (e: React.MouseEvent) => {
      if (!expandingItems.includes(element)) {
        expandingItems.push(element);
      } else {
        expandingItems = expandingItems.filter((x) => x != element);
      }
      setExpandingItems([...expandingItems]);
    };

    /**
     * 右クリックイベントハンドラ（onContextMenu）。
     * @param e イベントパラメータ。
     */
    const onContextMenu = (e: React.MouseEvent) => {
      showContextMenu(element, e);
    };

    /**
     * 開閉状態を取得。
     */
    const isOpen = useMemo(() => expandingItems.includes(element), [expandingItems.includes(element)]);

    /**
     * 外部から操作できる関数。
     */
    useImperativeHandle(ref, () => ({
      /**
       * 指定された要素を更新する。
       * @param target 対象要素。
       */
      updateElement: (target: Element) => {
        if (element == target) {
          updateComponentLocal();
        } else {
          childElementRefs.current.forEach((child, index) => {
            child.current?.updateElement(target);
          });
        }
      },
    }));

    return (
      <div
        className={
          'section-data-tree-node' +
          (element === selectedElement ? ' selected' : '') +
          (validationResult ? ' validation-passed' : ' validation-failed') +
          (ghost ? ' ghost' : '')
        }
        title={validationMessage}
      >
        <span ref={spanRef} />
        {!ghost && (
          <>
            <input type="checkbox" checked={isOpen} onChange={() => {}} />
            <span
              className={elementDefinition?.isEmpty || isDragging || ghost ? 'empty-node-icon' : 'node-icon'}
              onClick={handleToggleClick}
            />
          </>
        )}
        <label onClick={(e) => handleSelectClick(element, e)} onContextMenu={onContextMenu}>
          {tagName}
          {displayAttributesString}
          {!ghost && <span style={{ color: 'transparent' }}>{displayWidthString}</span>}
        </label>
        {ghost && (
          <>
            <span className="child-count">{childElements.length}</span>
          </>
        )}
        <div
          className="children"
          style={{
            display: isOpen && !isDragging ? 'block' : 'none',
          }}
        >
          {isOpen &&
            !ghost &&
            !isDragging &&
            childElements.map((x, index) => (
              <React.Fragment key={uuidv4()}>
                <Droppable node={x} position="before" draggingNode={draggingNode} />
                <SectionDataTreeNode
                  ref={childElementRefs.current[index]}
                  editor={editor}
                  element={x}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  expandingItems={expandingItems}
                  setExpandingItems={setExpandingItems}
                  handleSelectClick={handleSelectClick}
                  updateComponent={updateComponentLocal}
                  ghost={false}
                  draggingNode={draggingNode}
                  isDragging={isDragging || draggingNode === x}
                  showContextMenu={showContextMenu}
                  viewAttributes={viewAttributes}
                  scrollIntoElement={scrollIntoElement}
                  resetScrollIntoElement={resetScrollIntoElement}
                  validationResults={validationResults}
                />
              </React.Fragment>
            ))}

          {isOpen && !ghost && <Droppable node={element} position="end" draggingNode={draggingNode} />}
        </div>
      </div>
    );
  }
);
export { SectionDataTreeElementNode };
